import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Plus, Trash2, Edit, Loader2, X, Save, Upload, Image } from 'lucide-react';
import { brandsAPI, uploadAPI } from '../../services/api';

const AdminBrands = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [formData, setFormData] = useState({ name: '', slug: '', description: '', logo_url: '' });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    let isMounted = true;
    
    const fetchBrands = async () => {
      try {
        const response = await brandsAPI.getAll();
        if (isMounted) {
          setBrands(response.data);
        }
      } catch (error) {
        if (isMounted && error.name !== 'CanceledError') {
          console.error('Error fetching brands:', error);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    fetchBrands();
    
    return () => {
      isMounted = false;
    };
  }, []);

  const fetchBrands = async () => {
    try {
      const response = await brandsAPI.getAll();
      setBrands(response.data);
    } catch (error) {
      console.error('Error fetching brands:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (brandId) => {
    if (window.confirm('Delete this brand?')) {
      try {
        await brandsAPI.delete(brandId);
        setBrands(brands.filter(b => b._id !== brandId));
        alert('Brand deleted!');
      } catch (error) {
        console.error('Error deleting brand:', error);
        alert('Failed to delete brand');
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedBrands.length === 0) {
      alert('Select brands to delete');
      return;
    }
    if (window.confirm(`Delete ${selectedBrands.length} selected brands?`)) {
      try {
        await brandsAPI.bulkDelete(selectedBrands);
        setBrands(brands.filter(b => !selectedBrands.includes(b._id)));
        setSelectedBrands([]);
        alert('Brands deleted!');
      } catch (error) {
        console.error('Error bulk deleting brands:', error);
        alert('Failed to delete brands');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBrand) {
        await brandsAPI.update(editingBrand._id, formData);
        setBrands(brands.map(b => b._id === editingBrand._id ? { ...b, ...formData } : b));
        alert('Brand updated!');
      } else {
        const response = await brandsAPI.create(formData);
        setBrands([...brands, response.data]);
        alert('Brand created!');
      }
      closeModal();
    } catch (error) {
      console.error('Error saving brand:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to save brand';
      alert(errorMessage);
    }
  };

  const openEditModal = (brand) => {
    setEditingBrand(brand);
    setFormData({ name: brand.name, slug: brand.slug, description: brand.description || '', logo_url: brand.logo_url || '' });
    setShowAddModal(true);
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingBrand(null);
    setFormData({ name: '', slug: '', description: '', logo_url: '' });
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  };

  const toggleSelect = (brandId) => {
    setSelectedBrands(prev =>
      prev.includes(brandId) ? prev.filter(id => id !== brandId) : [...prev, brandId]
    );
  };

  const selectAll = () => {
    if (selectedBrands.length === brands.length) {
      setSelectedBrands([]);
    } else {
      setSelectedBrands(brands.map(b => b._id));
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-accent" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-charcoal font-heading">Brands</h1>
            <p className="text-stone-grey font-body">{brands.length} total brands</p>
          </div>
          <div className="flex gap-3">
            {selectedBrands.length > 0 && (
              <Button 
                onClick={handleBulkDelete}
                variant="outline" 
                className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white font-body"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Selected ({selectedBrands.length})
              </Button>
            )}
            <Button 
              className="bg-[#2C2824] hover:bg-[#1a1816] text-white font-body"
              onClick={() => setShowAddModal(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Brand
            </Button>
          </div>
        </div>

        {/* Add/Edit Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 shadow-xl">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-charcoal font-heading">
                  {editingBrand ? 'Edit Brand' : 'Add New Brand'}
                </h2>
                <button onClick={closeModal} className="text-stone-grey hover:text-charcoal">
                  <X className="h-6 w-6" />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-1 font-body">Brand Name</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({ 
                        ...formData, 
                        name: e.target.value,
                        slug: editingBrand ? formData.slug : generateSlug(e.target.value)
                      });
                    }}
                    placeholder="Enter brand name"
                    required
                    className="font-body"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-1 font-body">Slug</label>
                  <Input
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    placeholder="brand-slug"
                    required
                    className="font-body"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2 font-body">Logo</label>
                  
                  {/* Preview */}
                  {formData.logo_url && (
                    <div className="mb-3 p-3 bg-soft-sand/30 rounded-lg flex items-center gap-3">
                      <img 
                        src={formData.logo_url} 
                        alt="Logo preview" 
                        className="w-16 h-16 object-contain rounded border border-soft-sand bg-white"
                      />
                      <div className="flex-1 truncate text-sm text-stone-grey font-body">
                        {formData.logo_url.split('/').pop()}
                      </div>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="icon"
                        onClick={() => setFormData({ ...formData, logo_url: '' })}
                        className="text-red-500 hover:bg-red-50"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  
                  {/* Upload Button */}
                  <div className="flex gap-2">
                    <label className="flex-1">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          
                          setUploading(true);
                          try {
                            const response = await uploadAPI.uploadImage(file);
                            setFormData({ ...formData, logo_url: response.data.url });
                          } catch (error) {
                            console.error('Upload failed:', error);
                            alert('Failed to upload image');
                          } finally {
                            setUploading(false);
                          }
                        }}
                      />
                      <div className={`flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-soft-sand rounded-lg cursor-pointer hover:border-muted-green hover:bg-soft-sand/20 transition-colors ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
                        {uploading ? (
                          <Loader2 className="h-5 w-5 animate-spin text-muted-green" />
                        ) : (
                          <Upload className="h-5 w-5 text-stone-grey" />
                        )}
                        <span className="font-body text-stone-grey">
                          {uploading ? 'Uploading...' : 'Upload PNG/JPG'}
                        </span>
                      </div>
                    </label>
                  </div>
                  
                  {/* Or URL input */}
                  <div className="mt-2">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex-1 h-px bg-soft-sand"></div>
                      <span className="text-xs text-stone-grey font-body">or paste URL</span>
                      <div className="flex-1 h-px bg-soft-sand"></div>
                    </div>
                    <Input
                      value={formData.logo_url}
                      onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                      placeholder="https://example.com/logo.png"
                      className="font-body text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-1 font-body">Description (optional)</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brand description..."
                    rows={3}
                    className="w-full px-3 py-2 border border-soft-sand rounded-md font-body focus:border-muted-green focus:ring-muted-green"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={closeModal} className="flex-1 font-body">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-muted-green hover:bg-muted-green/90 text-white font-body">
                    <Save className="h-4 w-4 mr-2" />
                    {editingBrand ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg border border-soft-sand shadow-soft overflow-hidden">
          <table className="w-full">
            <thead className="bg-soft-sand">
              <tr>
                <th className="px-6 py-4 w-12">
                  <input
                    type="checkbox"
                    checked={selectedBrands.length === brands.length}
                    onChange={selectAll}
                    className="w-5 h-5"
                  />
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-charcoal font-body">Brand Name</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-charcoal font-body">Slug</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-charcoal font-body">Products</th>
                <th className="px-6 py-4 text-right text-sm font-semibold text-charcoal font-body">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-soft-sand">
              {brands.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-stone-grey font-body">
                    No brands yet. Click &quot;Add Brand&quot; to create one.
                  </td>
                </tr>
              ) : (
                brands.map((brand) => (
                  <tr key={brand._id} className="hover:bg-soft-sand/30 transition-colors">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedBrands.includes(brand._id)}
                        onChange={() => toggleSelect(brand._id)}
                        className="w-5 h-5"
                      />
                    </td>
                    <td className="px-6 py-4 font-medium text-charcoal font-body text-lg">{brand.name}</td>
                    <td className="px-6 py-4 text-sm text-stone-grey font-mono">{brand.slug}</td>
                    <td className="px-6 py-4 text-sm text-stone-grey font-body">{brand.product_count || 0}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="text-charcoal hover:bg-soft-sand"
                          onClick={() => openEditModal(brand)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="text-soft-terracotta hover:bg-soft-terracotta/10"
                          onClick={() => handleDelete(brand._id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminBrands;
